import { Box, Button, Divider, Modal, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { useTranslation } from 'react-i18next'
import closeModal from '../../assets/table_icons/delete.svg'
import deleteFamiliar from '../../assets/users/deleteFamiliar.svg'
import whatsappIcon from '../../assets/users/whatsapp.svg'
import styles from './ModalFamiliar.module.css'
import { GenericTooltip } from 'components/generic-tooltip'
import { emptyUserDTO, User, UserDTO } from 'modules/users/models/User'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { LoggedUserService } from 'modules/users/services/LoggedUserService'
import { getUserContainer } from 'container/user-module'
import { Query } from 'common/api/Query'
import { PhoneInputWithCountrySelect } from '../../components/phone-input/PhoneInputWithCountrySelect'
import { PatientFamilyDTO, emptyPatientFamily } from 'modules/users/models/PatientFamily'

interface FamiliarWithUser {
  family: PatientFamilyDTO
  user: User
}

interface ModalFamiliarProps {
  open: boolean
  onClose: () => void
  patientID: string
}

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function ModalFamiliar({ open, onClose, patientID }: ModalFamiliarProps) {
  const { t } = useTranslation()
  const [familiares, setFamiliares] = useState<FamiliarWithUser[]>([])
  const [showForm, setShowForm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [existingUser, setExistingUser] = useState<User | null>(null)
  const [isAlreadyFamiliar, setIsAlreadyFamiliar] = useState(false)
  const [isNonFamiliarUser, setIsNonFamiliarUser] = useState(false)
  const loggedUser = loggedUserService.get()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: '',
  })

  const getFamiliars = () => {
    setIsLoading(true)
    userService.getFamilies(patientID).subscribe((families) => {
      if (families.length === 0) {
        setFamiliares([])
        setIsLoading(false)
        return
      }

      const familiarIDs = families.map((f) => f.familiarID)
      userService
        .getFilteredList(
          new Query({
            query: [{ name: 'ids', value: familiarIDs }],
            pager: { offset: 0, limit: familiarIDs.length },
          })
        )
        .subscribe((res) => {
          const familiaresWithUsers = families
            .map((family) => {
              const user = res.items.find((u) => u.id === family.familiarID)
              return user ? { family, user } : null
            })
            .filter(Boolean) as FamiliarWithUser[]

          setFamiliares(familiaresWithUsers)
          setIsLoading(false)
        })
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'email') {
      setFormData({ ...formData, email: value })
      setExistingUser(null)
      setIsAlreadyFamiliar(false)
      setIsNonFamiliarUser(false)
    } else {
      setFormData({ ...formData, [name]: value })
    }

    if (name === 'email' && value.includes('@')) {
      userService
        .getFilteredList(
          new Query({
            query: [{ name: 'email', value: value }],
            pager: { offset: 0, limit: 1 },
          })
        )
        .subscribe((res) => {
          if (res.items.length > 0) {
            const foundUser = res.items[0]

            const isFamiliar = foundUser.roles.some((role) => role.toLowerCase() === 'familiar')

            if (!isFamiliar) {
              setExistingUser(null)
              setIsAlreadyFamiliar(false)
              setIsNonFamiliarUser(true)
              return
            }

            setExistingUser(foundUser)
            setIsNonFamiliarUser(false)

            const isAlreadyRelated = familiares.some((f) => f.user.id === foundUser.id)
            setIsAlreadyFamiliar(isAlreadyRelated)

            if (!isAlreadyRelated) {
              userService.getPatientsByFamiliar(foundUser.id).subscribe((families) => {
                const phoneFromRelation = families.length > 0 ? families[0].phone : ''

                setFormData((prev) => ({
                  ...prev,
                  firstName: foundUser.firstName,
                  lastName: foundUser.lastName,
                  email: foundUser.email,
                  phone: phoneFromRelation,
                }))
              })
            }
          } else {
            setExistingUser(null)
            setIsAlreadyFamiliar(false)
            setIsNonFamiliarUser(false)
          }
        })
    }
  }

  const handlePhoneChange = (phone: string) => {
    setFormData({ ...formData, phone })
  }

  const handleAddFamiliar = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      relationship: '',
    })
    setExistingUser(null)
    setIsAlreadyFamiliar(false)
    setIsNonFamiliarUser(false)
    setShowForm(true)
  }

  const handleClose = () => {
    setShowForm(false)
    onClose()
  }

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      return false
    }
    if (isAlreadyFamiliar || isNonFamiliarUser) {
      return false
    }
    return true
  }

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    if (existingUser) {
      handleAddFamilyRelation(existingUser.id)
    } else {
      const newFamiliar: UserDTO = {
        ...emptyUserDTO(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        language: loggedUser?.language ?? 1,
        isPatient: false,
        isActive: true,
        patologies: [],
      }

      userService.addFamiliar(newFamiliar, navigator.language).subscribe((res) => {
        if (res) {
          handleAddFamilyRelation(res.id)
        }
      })
    }
  }

  const handleAddFamilyRelation = (familiarID: string) => {
    const familyRelation: PatientFamilyDTO = {
      ...emptyPatientFamily(patientID),
      familiarID: familiarID,
      relationship: formData.relationship,
      phone: formData.phone,
    }

    userService.addFamilyRelation(familyRelation).subscribe(() => {
      getFamiliars()
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        relationship: '',
      })
      setShowForm(false)
    })
  }

  const handleRemoveFamiliar = (familiarID: string) => {
    userService.removeFamilyRelation(patientID, familiarID).subscribe(() => {
      getFamiliars()
    })
  }

  const handleOpenWhatsApp = (phoneNumber: string) => {
    if (!phoneNumber) return

    const cleanPhone = phoneNumber.replace(/\D/g, '')
    window.open(`https://web.whatsapp.com/send?phone=${cleanPhone}`, '_blank')
  }

  useEffect(() => {
    if (open) {
      getFamiliars()
    }
  }, [open, patientID])

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modalContainer}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" className={styles.title}>
            {t('modalFamiliarsTitle')}
          </Typography>
          <GenericTooltip
            values={[t('close')]}
            icon={
              <img
                src={closeModal}
                onClick={handleClose}
                className={styles.icon}
                alt={t('close')}
              />
            }
            noUseFab={true}
          />
        </Box>

        <Box>
          {isLoading && <p>{t('loadingFamiliars')}</p>}
          {!isLoading && (
            <>
              {familiares.map(({ family, user }) => (
                <Box key={family.id} className={styles.familiarItem}>
                  <Box className={styles.familiarName}>
                    {user.firstName} {user.lastName}
                    {family.relationship && ` (${family.relationship})`}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src={whatsappIcon}
                      onClick={() => handleOpenWhatsApp(family.phone)}
                      className={styles.icon}
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                      title={t('openWhatsApp')}
                      alt={t('openWhatsApp')}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {family.phone}
                    </Typography>
                    <img
                      src={deleteFamiliar}
                      onClick={() => handleRemoveFamiliar(user.id)}
                      className={styles.icon}
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                      title={t('remove')}
                      alt={t('remove')}
                    />
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>

        {familiares.length > 0 && <Divider className={styles.divider} />}

        {!showForm && (
          <Button
            onClick={handleAddFamiliar}
            className={styles.buttonAdd}
            sx={{ marginBottom: '1rem' }}
          >
            <span className={styles.buttonAddIcon}>+</span>
            {t('addFamiliar')}
          </Button>
        )}

        {showForm && (
          <Box sx={{ mt: 2 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
            >
              <Box>
                <Box mb={2}>
                  <Typography mb={1}>{t('email')}</Typography>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    required
                  />
                  {isAlreadyFamiliar && (
                    <Typography color="error" variant="caption" display="block" mt={1}>
                      {t('userAlreadyFamiliar')}
                    </Typography>
                  )}
                  {isNonFamiliarUser && (
                    <Typography color="error" variant="caption" display="block" mt={1}>
                      {t('emailBelongsToNonFamiliar')}
                    </Typography>
                  )}
                  {existingUser && !isAlreadyFamiliar && !isNonFamiliarUser && (
                    <Typography color="info.main" variant="caption" display="block" mt={1}>
                      {t('userFound')}
                    </Typography>
                  )}
                </Box>

                <Box mb={2} display="flex" justifyContent="space-between" gap={2}>
                  <Box width="50%">
                    <Typography mb={1}>{t('name')}</Typography>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      required
                      disabled={existingUser !== null}
                    />
                  </Box>

                  <Box width="50%">
                    <Typography mb={1}>{t('lastName')}</Typography>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      required
                      disabled={existingUser !== null}
                    />
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography mb={1}>{t('relationship')}</Typography>
                  <Input
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                  />
                </Box>

                <Box mb={2}>
                  <PhoneInputWithCountrySelect
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    label={t('phone')}
                    required
                    disabled={existingUser !== null}
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" gap={2} mt={3}>
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={styles.buttonCancel}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className={styles.buttonAdd}
                  disabled={isAlreadyFamiliar || isNonFamiliarUser}
                >
                  {t('save')}
                </Button>
              </Box>
            </form>
          </Box>
        )}
      </Box>
    </Modal>
  )
}
